// client/src/components/react-dashboard/apis_dashboard/APIPlanManager.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Tooltip,
  IconButton,
  useTheme
} from '@mui/material';
import {
  ContentCopy, Key, VpnKeyOff, Settings, ExpandMore, ExpandLess, Close,
  Science, Bolt, WorkspacePremium, Tune, ArrowBack, DeleteOutline
} from '@mui/icons-material';

import {
  useSetOrgApiPlanMutation,
  useRotateOrgApiKeyMutation,
  useRevokeOrgApiKeyMutation,
  useCreateOrgApiKeyMutation,
  useDeleteOrgApiPlanMutation
} from '../../../features/organizations/organizationsApiSlice';

import { tokens } from '../../../theme';

const copy = async (text, onDone) => {
  try { await navigator.clipboard.writeText(text); onDone?.('Copied'); }
  catch { onDone?.('Copy failed'); }
};


const PLAN_TILES = [
  {
    key: 'testing',
    title: 'Testing',
    icon: <Science fontSize="small" />,
    main: '200 / day',
    sub: (
      <>
        <div>≈ 6,000 / month</div>
        <div>Default throttle (10 RPS)</div>
        <div>Default burst (50)</div>
      </>
    ),
    meta: { period: 'DAY', creditsLimit: 200, rateRps: 10, burst: 50 },
  },
  {
    key: 'basic',
    title: 'Basic',
    icon: <Bolt fontSize="small" />,
    main: '100,000 / month',
    sub: (
      <>
        <div>Throttle: 500 RPS</div>
        <div>Burst: 1,000</div>
      </>
    ),
    meta: { period: 'MONTH', creditsLimit: 100000, rateRps: 500, burst: 1000 },
  },
  {
    key: 'enterprise',
    title: 'Enterprise',
    icon: <WorkspacePremium fontSize="small" />,
    main: '1,000,000 / month',
    sub: (
      <>
        <div>Throttle: 5,000 RPS</div>
        <div>Burst: 10,000</div>
      </>
    ),
    meta: { period: 'MONTH', creditsLimit: 1000000, rateRps: 5000, burst: 10000 },
  },
];

const PERIOD_OPTIONS_V2 = [
  { value: 'DAY', label: 'Daily' },
  { value: 'MONTH', label: 'Monthly' },
];

const APIPlanManager = ({ organization, onSnack }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const gradientBg = `linear-gradient(135deg, ${colors.primary[500]}, ${colors.secondary[800]})`;

  const shellSx = {
    p: 2.5,
    borderRadius: 2,
    background: gradientBg,
    // makes the gradient look nicer inside dense pages
    boxShadow: theme.palette.mode === 'dark'
      ? '0 10px 30px rgba(0,0,0,0.45)'
      : '0 10px 30px rgba(0,0,0,0.15)',
  };

  const cardSx = {
    p: 2,
    borderRadius: 2,
    border: theme.palette.mode === 'dark'
      ? '1px solid rgba(255,255,255,0.12)'
      : '1px solid rgba(255,255,255,0.55)',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(10,12,18,0.45)'
      : 'rgba(255,255,255,0.72)',
    backdropFilter: 'blur(10px)',
  };

  const titleSx = { color: 'text.primary', fontWeight: 700 };
  const subtitleSx = { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.60)' };

  const orgId = organization?.organization_id;
  const cfg = organization?.api_config || {};
  const serverKeyId = cfg?.api_key_id || null;

  const [localKeyId, setLocalKeyId] = useState(null);
  const [newKeyValue, setNewKeyValue] = useState(''); // show-once buffer
  const [showAdvanced, setShowAdvanced] = useState(false);

  // keep localKeyId in sync with server, but don't clobber immediately after we just updated locally
  useEffect(() => {
    setLocalKeyId(serverKeyId || null);
  }, [serverKeyId, orgId]);

  const effectiveKeyId = localKeyId || null;

  const currentPlan = useMemo(() => ({
    plan_tier: cfg?.plan_tier || cfg?.tier || '—',
    quota_period: cfg?.quota_period || cfg?.quotaPeriod || '—',
    quota: cfg?.quota ?? cfg?.monthQuota ?? null,
    rate_rps: cfg?.rate_rps ?? cfg?.rateRps ?? null,
    usage_plan_id: cfg?.usage_plan_id || '—',
    api_key_id: effectiveKeyId || '-',
    enabled: cfg?.enabled,
  }), [cfg, effectiveKeyId]);

  const hasAnyPlanSignals =
    !!cfg?.plan ||
    !!cfg?.tier ||
    !!cfg?.usage_plan_id ||
    !!cfg?.credit_ledger?.period ||
    (cfg?.credit_ledger?.credits_limit != null);

  const inferredPlan =
    cfg?.plan ||
    ((cfg?.credit_ledger?.period === 'DAY' && Number(cfg?.credit_ledger?.credits_limit) === 200)
      ? 'testing'
      : undefined);

  const serverPlanRaw = (cfg?.plan || cfg?.tier || inferredPlan || null);
  const serverPlan = serverPlanRaw ? String(serverPlanRaw).toLowerCase() : null;

  // If the org has no plan configured, don't force it to testing.
  const noPlan = !hasAnyPlanSignals;

  const hasPlanConfigured = !noPlan;
  const [hasPlanConfiguredUI, setHasPlanConfiguredUI] = useState(null);
  const effectiveHasPlanConfigured = hasPlanConfiguredUI ?? hasPlanConfigured;
  const effectiveNoPlan = !effectiveHasPlanConfigured;
  const [planMode, setPlanMode] = useState(serverPlan === 'custom' ? 'custom' : 'plans');

  const [selectedPlan, setSelectedPlan] = useState(() => {
    if (noPlan) return null; // nothing selected
    return PLAN_TILES.some(p => p.key === serverPlan) ? serverPlan : 'testing';
  });
  // custom fields
  const [customPeriod, setCustomPeriod] = useState((cfg?.credit_ledger?.period || 'MONTH').toUpperCase());
  const [customCreditsLimit, setCustomCreditsLimit] = useState(
    cfg?.credit_ledger?.credits_limit != null ? String(cfg.credit_ledger.credits_limit) : ''
  );
  const [customRateRps, setCustomRateRps] = useState(cfg?.rate_rps != null ? String(cfg.rate_rps) : '');
  const [customBurst, setCustomBurst] = useState(cfg?.burst != null ? String(cfg.burst) : '');

  const [setPlan, { isLoading: setPlanBusy }] = useSetOrgApiPlanMutation();
  const [rotateKey, { isLoading: rotateBusy }] = useRotateOrgApiKeyMutation();
  const [revokeKey, { isLoading: revokeBusy }] = useRevokeOrgApiKeyMutation();
  const [createKey, { isLoading: createBusy }] = useCreateOrgApiKeyMutation();
  const [deletePlan, { isLoading: deletePlanBusy }] = useDeleteOrgApiPlanMutation();
  const canRemovePlan = !effectiveKeyId;
  const handleSnack = (msg) => onSnack?.(msg);

  // what the server currently has (for "dirty" comparison)
  const [savedSnapshot, setSavedSnapshot] = useState({
    mode: serverPlan === 'custom' ? 'custom' : 'plans',
    plan: PLAN_TILES.some(p => p.key === serverPlan) ? serverPlan : 'testing',
    period: (cfg?.credit_ledger?.period || 'MONTH').toUpperCase(),
    creditsLimit: cfg?.credit_ledger?.credits_limit != null ? String(cfg.credit_ledger.credits_limit) : '',
    rateRps: cfg?.rate_rps != null ? String(cfg.rate_rps) : '',
    burst: cfg?.burst != null ? String(cfg.burst) : '',
  });

  // dirty flag (unsaved local changes)
  const planDirty = useMemo(() => {
    if (planMode !== savedSnapshot.mode) return true;

    if (planMode === 'plans') {
      return selectedPlan !== savedSnapshot.plan;
    }

    // custom
    return (
      customPeriod !== savedSnapshot.period ||
      String(customCreditsLimit || '') !== String(savedSnapshot.creditsLimit || '') ||
      String(customRateRps || '') !== String(savedSnapshot.rateRps || '') ||
      String(customBurst || '') !== String(savedSnapshot.burst || '')
    );
  }, [
    planMode,
    selectedPlan,
    customPeriod,
    customCreditsLimit,
    customRateRps,
    customBurst,
    savedSnapshot
  ]);
  const canSave =
    !!orgId &&
    planDirty &&
    (planMode === 'custom' || !!selectedPlan) &&
    !setPlanBusy &&
    !deletePlanBusy;

const onSavePlanInline = async () => {
  try {
    if (planMode === 'custom') {
      await setPlan({ /* ... */ }).unwrap();

      // UI: plan now exists (even before server refetch)
      setHasPlanConfiguredUI(true);

      setSavedSnapshot({
        mode: 'custom',
        plan: null,
        period: customPeriod,
        creditsLimit: String(customCreditsLimit || ''),
        rateRps: String(customRateRps || ''),
        burst: String(customBurst || ''),
      });
    } else {
      await setPlan({ id: orgId, plan: selectedPlan }).unwrap();

      // UI: plan now exists
      setHasPlanConfiguredUI(true);

      setSavedSnapshot({
        mode: 'plans',
        plan: selectedPlan,
        period: savedSnapshot.period,
        creditsLimit: savedSnapshot.creditsLimit,
        rateRps: savedSnapshot.rateRps,
        burst: savedSnapshot.burst,
      });
    }

    handleSnack('Plan updated');
  } catch (e) {
    handleSnack(e?.data?.message || 'Failed to set plan');
  }
};


const onRemovePlanInline = async () => {
  try {
    await deletePlan({ id: orgId }).unwrap();

    setHasPlanConfiguredUI(false); // <-- immediate UI

    setSelectedPlan(null);
    setPlanMode('plans');
    setSavedSnapshot({
      mode: 'plans',
      plan: null,
      period: (cfg?.credit_ledger?.period || 'MONTH').toUpperCase(),
      creditsLimit: '',
      rateRps: '',
      burst: '',
    });

    handleSnack('Plan removed');
  } catch (e) {
    handleSnack(e?.data?.message || 'Failed to remove plan');
  }
};

  const onGenerate = async () => {
    try {
      const resp = await createKey({ id: orgId }).unwrap();
      // resp should include api_key and api_key_id
      setLocalKeyId(resp?.api_key_id || null);
      setNewKeyValue(resp?.api_key || '');
      handleSnack(resp?.api_key ? 'API key created (shown once).' : 'Key created.');
    } catch (e) {
      handleSnack(e?.data?.message || 'Failed to create key');
    }
  };

  const onRotate = async () => {
    try {
      const resp = await rotateKey({ id: orgId }).unwrap();
      setLocalKeyId(resp?.api_key_id || null);
      setNewKeyValue(resp?.api_key || '');
      handleSnack(resp?.api_key ? 'Key rotated (shown once).' : 'Key rotated.');
    } catch (e) {
      handleSnack(e?.data?.message || 'Failed to rotate key');
    }
  };

  const onRevoke = async () => {
    try {
      await revokeKey({ id: orgId }).unwrap();
      setLocalKeyId(null);
      setNewKeyValue('');
      handleSnack('API key revoked');
    } catch (e) {
      handleSnack(e?.data?.message || 'Failed to revoke key');
    }
  };


  useEffect(() => {
    const serverHasPlanSignals =
      !!cfg?.plan ||
      !!cfg?.tier ||
      !!cfg?.usage_plan_id ||
      !!cfg?.credit_ledger?.period ||
      (cfg?.credit_ledger?.credits_limit != null);

    const spRaw = (cfg?.plan || cfg?.tier || inferredPlan || null);
    const sp = spRaw ? String(spRaw).toLowerCase() : null;

    if (!serverHasPlanSignals) {
      setPlanMode('plans');
      setSelectedPlan(null); // <-- NONE selected
    } else if (sp === 'custom') {
      setPlanMode('custom');
    } else {
      setPlanMode('plans');
      setSelectedPlan(PLAN_TILES.some(p => p.key === sp) ? sp : 'testing');
    }

    setCustomPeriod((cfg?.credit_ledger?.period || 'MONTH').toUpperCase());
    setCustomCreditsLimit(cfg?.credit_ledger?.credits_limit != null ? String(cfg.credit_ledger.credits_limit) : '');
    setCustomRateRps(cfg?.rate_rps != null ? String(cfg.rate_rps) : '');
    setCustomBurst(cfg?.burst != null ? String(cfg.burst) : '');
    setSavedSnapshot({
      mode: sp === 'custom' ? 'custom' : 'plans',
      plan: serverHasPlanSignals
        ? (PLAN_TILES.some(p => p.key === sp) ? sp : 'testing')
        : null,
      period: (cfg?.credit_ledger?.period || 'MONTH').toUpperCase(),
      creditsLimit: cfg?.credit_ledger?.credits_limit != null ? String(cfg.credit_ledger.credits_limit) : '',
      rateRps: cfg?.rate_rps != null ? String(cfg.rate_rps) : '',
      burst: cfg?.burst != null ? String(cfg.burst) : '',
    });
    setHasPlanConfiguredUI(null);
  }, [orgId, cfg?.plan, cfg?.tier, inferredPlan, cfg?.usage_plan_id, cfg?.credit_ledger?.period, cfg?.credit_ledger?.credits_limit]);


  const tileSx = (active) => ({
    p: 1.5,
    borderRadius: 2,
    cursor: 'pointer',
    height: '100%',
    border: active
      ? `1px solid ${colors.primary[300]}`
      : theme.palette.mode === 'dark'
        ? '1px solid rgba(255,255,255,0.14)'
        : '1px solid rgba(0,0,0,0.10)',
    background: active
      ? (theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.10)'
        : 'rgba(255,255,255,0.92)')
      : (theme.palette.mode === 'dark'
        ? 'rgba(10,12,18,0.35)'
        : 'rgba(255,255,255,0.72)'),
    backdropFilter: 'blur(10px)',
    transition: 'transform 120ms ease, border-color 120ms ease',
    '&:hover': { transform: 'translateY(-1px)' },
  });

  const tilesGridSx = {
    display: 'grid',
    gap: 1.25,
    gridTemplateColumns: {
      xs: '1fr',                 // phone
      sm: '1fr 1fr',             // 2 across
      md: 'repeat(4, minmax(0, 1fr))', // 4 across: testing/basic/enterprise/custom
    },
    alignItems: 'stretch',
  };


  return (
    <Paper elevation={6} sx={shellSx}>
      <Box sx={{ display: 'grid', gap: 2 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box>
            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
              API Access & Billing
            </Typography>

            <Typography variant="body2" sx={{ mt: 0.5, color: subtitleSx.color }}>
              Manage API keys, rate limits, and credit usage for this organization.
            </Typography>
          </Box>

          <IconButton
            onClick={() => onSnack?.('__CLOSE_DIALOG__')}
            sx={{ color: 'text.primary' }}
          >
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ opacity: 0.3 }} />
        {/* Plan & Limits (inline tiles) */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={titleSx}>Plan & Limits</Typography>
              {effectiveNoPlan && !planDirty ? (
                <Box sx={{
                  mt: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.70)',
                  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.16)' : '1px solid rgba(0,0,0,0.10)',
                }}>
                  No plan configured
                </Box>
              ) : null}
              <Typography variant="body2" sx={subtitleSx}>
                Select a plan, then click Save.
              </Typography>
              {planDirty ? (
                <Box sx={{
                  mt: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? colors.redAccent[200] : 'rgba(0,0,0,0.75)',
                  background: theme.palette.mode === 'dark'
                    ? colors.redAccent[700] + '22'
                    : 'rgba(0,0,0,0.06)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.18)'
                    : '1px solid rgba(0,0,0,0.10)',
                }}>
                  Unsaved changes!
                </Box>
              ) : null}
              
            </Box>
          </Box>

          <Divider sx={{ my: 2, opacity: theme.palette.mode === 'dark' ? 0.25 : 0.4 }} />

          {/* tiles row */}
          {planMode === 'plans' ? (
            
            <Box sx={tilesGridSx}>
              
              {PLAN_TILES.map((p) => {
                const active = selectedPlan === p.key;
                return (
                  <Paper
                    key={p.key}
                    variant="outlined"
                    onClick={() => setSelectedPlan(p.key)}
                    sx={tileSx(active)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                      {/* LEFT: must be allowed to shrink */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                        <Box sx={{
                          width: 34, height: 34, borderRadius: 2,
                          display: 'grid', placeItems: 'center',
                          background: active ? `rgba(255,255,255,0.18)` : `rgba(255,255,255,0.08)`,
                          border: active ? `1px solid rgba(255,255,255,0.22)` : `1px solid rgba(255,255,255,0.12)`,
                          flexShrink: 0, // icon never shrinks
                        }}>
                          {p.icon}
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
                            {p.title}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.85 }} wrap>
                            {p.main}
                          </Typography>
                        </Box>
                      </Box>

                      {/* RIGHT: never shrink */}
                      {active ? (
                        <Box sx={{
                          px: 1, py: 0.25, borderRadius: 999,
                          fontSize: 12, fontWeight: 700,
                          background: `rgba(255,255,255,0.16)`,
                          border: `1px solid rgba(255,255,255,0.20)`,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}>
                          {planDirty ? 'Pending' : 'Selected'}
                        </Box>
                      ) : null}
                    </Box>

                    <Box sx={{ mt: 1, opacity: 0.9, fontSize: 12.5, lineHeight: 1.35 }}>
                      {p.sub}
                    </Box>
                  </Paper>
                );
              })}

              {/* Custom tile */}
              <Paper
                variant="outlined"
                onClick={() => setPlanMode('custom')}
                sx={tileSx(false)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: 2,
                    display: 'grid', placeItems: 'center',
                    background: `rgba(255,255,255,0.08)`,
                    border: `1px solid rgba(255,255,255,0.12)`
                  }}>
                    <Tune />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }}>Custom</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      Daily/monthly credits + throttle
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                  For special cases.
                </Typography>
              </Paper>
            </Box>
          ) : (
            // custom editor inline
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography sx={{ fontWeight: 800 }}>Custom plan</Typography>
                <Button
                  size="small"
                  startIcon={<ArrowBack />}
                  onClick={() => setPlanMode('plans')}
                  sx={{
                    color: colors.primary[200],
                    '&:hover': { backgroundColor: 'transparent', color: colors.primary[100] },
                  }}
                >
                  Back to plans
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  select
                  size="small"
                  label="Credits period"
                  value={customPeriod}
                  onChange={(e) => setCustomPeriod(e.target.value)}
                >
                  {PERIOD_OPTIONS_V2.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label={`Credits limit (${customPeriod === 'DAY' ? 'per day' : 'per month'})`}
                  value={customCreditsLimit}
                  onChange={(e) => setCustomCreditsLimit(e.target.value)}
                  inputMode="numeric"
                />
              </Box>

              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  size="small"
                  label="Throttle rate (RPS)"
                  value={customRateRps}
                  onChange={(e) => setCustomRateRps(e.target.value)}
                  inputMode="numeric"
                  helperText="API Gateway safety rail."
                />
                <TextField
                  size="small"
                  label="Throttle burst"
                  value={customBurst}
                  onChange={(e) => setCustomBurst(e.target.value)}
                  inputMode="numeric"
                  helperText="Short spikes allowed."
                />
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2, opacity: theme.palette.mode === 'dark' ? 0.25 : 0.4 }} />

          {/* actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip
              title={
                !canRemovePlan
                  ? 'Revoke API key before removing plan.'
                  : !effectiveHasPlanConfigured
                    ? 'No plan to remove.'
                    : ''
              }
              disableHoverListener={canRemovePlan && effectiveHasPlanConfigured}
            >
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={onRemovePlanInline}
                  disabled={
                    !canRemovePlan ||
                    !effectiveHasPlanConfigured ||
                    deletePlanBusy ||
                    setPlanBusy
                  }
                >
                  Remove plan
                </Button>
              </span>
            </Tooltip>

            <Button
              variant={planDirty ? 'contained' : 'outlined'}
              onClick={onSavePlanInline}
              disabled={!canSave}
            >
              {(setPlanBusy || deletePlanBusy) ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {planDirty ? 'Save changes' : 'Saved'}
            </Button>
          </Box>

          {!canRemovePlan ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
              Remove plan is only available when the organization has no active API key.
            </Typography>
          ) : null}
        </Paper>


        {/* Key management */}
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="h6" sx={titleSx}>API Key</Typography>
          <Typography variant="body2" sx={subtitleSx}>Rotate or revoke.</Typography>

          <Divider sx={{ my: 2, opacity: theme.palette.mode === 'dark' ? 0.25 : 0.4 }} />


          <Box sx={{ display: 'grid', gap: 1 }}>
            <Row
              k="API key"
              v={newKeyValue ? 'Shown below (one time)' : (effectiveKeyId ? 'Hidden' : 'None')}
            />

            {newKeyValue ? (
              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: 1.5,
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.14)'
                    : '1px solid rgba(0,0,0,0.10)',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(0,0,0,0.35)'
                    : 'rgba(255,255,255,0.85)',
                }}
              >
                <Box sx={{ overflowX: 'auto', fontFamily: 'monospace', fontSize: 13, color: 'text.primary' }}>

                  {newKeyValue}
                </Box>
                <Tooltip title="Copy key">
                  <IconButton
                    size="small"
                    onClick={() => copy(newKeyValue, handleSnack)}
                    sx={{ ml: 1 }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Paper>
            ) : null}
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAdvanced((s) => !s)}
              startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
              sx={{
                justifySelf: 'end',
                px: 0,
                color: colors.primary[200],
                fontWeight: 600,
                textShadow: theme.palette.mode === 'dark'
                  ? `0 0 6px ${colors.primary[200]}55`
                  : 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: colors.greenAccent[300],
                },
              }}
            >
              {showAdvanced ? 'Hide advanced' : 'Show advanced'}
            </Button>

            {showAdvanced ? (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Row k="API key ID (internal)" v={currentPlan.api_key_id} copyable />
                <Row k="Usage plan ID" v={currentPlan.usage_plan_id} copyable />
              </Box>
            ) : null}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {effectiveKeyId ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Key />}
                    onClick={onRotate}
                    disabled={!orgId || rotateBusy}
                  >
                    Rotate key
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<VpnKeyOff />}
                    onClick={onRevoke}
                    disabled={!orgId || revokeBusy}
                  >
                    Revoke key
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Key />}
                  onClick={onGenerate}
                  disabled={!orgId || createBusy}
                >
                  Generate new key
                </Button>
              )}
            </Box>
          </Box>

        </Paper >


      </Box >
    </Paper >
  );
};

const Row = ({ k, v, copyable }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
    <Typography variant="body2" color="text.secondary">{k}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontFamily: copyable ? 'monospace' : 'inherit' }}>
        {v}
      </Typography>
      {copyable && v && v !== '—' ? (
        <Tooltip title="Copy">
          <IconButton size="small" onClick={() => copy(v)}><ContentCopy fontSize="inherit" /></IconButton>
        </Tooltip>
      ) : null}
    </Box>
  </Box>
);

export default APIPlanManager;
